import { filterSupportedPaymentMethodTypes } from '@/consts/payment-method-types';
import { resolveDefaultPaymentMethod } from '@/utils/resolveDefaultPaymentMethod';

export const SUBSCRIPTION_ALLOWED_METHODS_V1 = [
  'pix',
  'credit_card',
  'pix_auto',
  'paypal_wallet',
];

// O Pix comum fica na assinatura mesmo sem renovar sozinho (cada ciclo vira uma cobrança que o
// comprador paga na mão): tirá-lo gerou mais atrito com o produtor do que resolveu.
export const SUBSCRIPTION_ALLOWED_METHODS = ['credit_card', 'pix_auto', 'pix', 'paypal_wallet'];

// Forward-compat for multicurrency; no call site passes currency yet (defaults to BRL).
export const USD_ALLOWED_METHODS = [];

export const UNIQUE_PAYMENT_METHODS = [
  'credit_card',
  'pix',
  'pagaleve',
  'paypal_wallet',
  'picpay',
  'googlepay',
  'applepay',
  'openfinance_nubank',
  // Pagamento dividido: so em pagamento unico. Fora de SUBSCRIPTION_ALLOWED_METHODS
  // de proposito — o backend recusa dividir assinatura (duas partes criariam duas
  // recorrencias para a mesma venda), entao oferecer aqui produziria um produto que
  // o checkout recusa na hora de pagar.
  'multi_card',
  'card_pix',
];

export function getAllowedPaymentMethodTypes({ productType, currency = 'BRL' } = {}) {
  const normalizedCurrency = String(currency || 'BRL').trim().toUpperCase();

  if (normalizedCurrency === 'USD') {
    return USD_ALLOWED_METHODS;
  }

  if (productType === 'subscription') {
    return SUBSCRIPTION_ALLOWED_METHODS;
  }

  return UNIQUE_PAYMENT_METHODS;
}

export function pickKnownPaymentMethodTypes(allowedMethods, knownTypes) {
  if (!Array.isArray(allowedMethods)) return [];

  if (!Array.isArray(knownTypes) || knownTypes.length === 0) {
    return [...allowedMethods];
  }

  return allowedMethods.filter((method) => knownTypes.includes(method));
}

export function normalizePaymentMethodsByProductType(
  productType,
  selectedMethods = [],
  currency = 'BRL'
) {
  if (!Array.isArray(selectedMethods)) return [];

  const allowedTypes = getAllowedPaymentMethodTypes({ productType, currency });

  return filterSupportedPaymentMethodTypes(
    selectedMethods.filter((method) => allowedTypes.includes(method))
  );
}

/**
 * Recorta os métodos do payload aos que a CONTA pode usar, antes de enviar.
 *
 * As listas por tipo de produto dizem o que o produto aceita, não o que a conta tem: método em
 * piloto (`paypal_wallet`) passa por elas. A tela só mostra os métodos da conta, então um método
 * de fora que sobrou no formulário não tem como ser desmarcado, e o backend recusa o cadastro
 * inteiro com "Métodos de pagamento inválidos". Sem a lista da conta carregada, nada é recortado.
 */
export function restrictPaymentMethodsToAccount(payload, accountMethodTypes) {
  if (!Array.isArray(accountMethodTypes) || accountMethodTypes.length === 0) {
    return payload;
  }

  const keepAccountMethods = (methods) =>
    Array.isArray(methods) ? methods.filter((method) => accountMethodTypes.includes(method)) : methods;

  const paymentMethods = keepAccountMethods(payload.paymentMethods);
  const restricted = { ...payload, paymentMethods };

  if (Array.isArray(payload.paymentsOrder)) {
    restricted.paymentsOrder = keepAccountMethods(payload.paymentsOrder);
  }

  if (
    payload.defaultPaymentMethod &&
    Array.isArray(paymentMethods) &&
    !paymentMethods.includes(payload.defaultPaymentMethod)
  ) {
    restricted.defaultPaymentMethod = resolveDefaultPaymentMethod(paymentMethods, accountMethodTypes);
  }

  return restricted;
}

/**
 * Resolve métodos e método padrão quando o produtor troca o tipo do produto.
 *
 * `availableMethodTypes` são os tipos que a CONTA pode usar. Passar `null` (ou omitir) mantém o
 * comportamento antigo — a lista de assinatura inteira —, e existe para o caso em que os métodos
 * ainda não carregaram; com um array, a pré-seleção é a interseção dos dois.
 */
export function resolvePaymentMethodsOnPaymentTypeChange(
  nextPaymentType,
  previousPaymentType,
  currentMethods = [],
  currentDefaultMethod = '',
  savedUniquePaymentState = null,
  availableMethodTypes = null
) {
  const methods = Array.isArray(currentMethods) ? currentMethods : [];

  if (nextPaymentType === 'subscription' && previousPaymentType === 'unique') {
    const savedUniqueState =
      methods.length > 0
        ? { methods, defaultMethod: currentDefaultMethod }
        : savedUniquePaymentState;

    // SUBSCRIPTION_ALLOWED_METHODS diz o que a ASSINATURA aceita, não o que a CONTA pode usar.
    // Semear a constante crua colocava no formulário método restrito a piloto (paypal_wallet),
    // que não aparece na tela e o produtor não tem como desmarcar: o backend recusava o cadastro
    // inteiro com "Métodos de pagamento inválidos".
    const subscriptionMethods = Array.isArray(availableMethodTypes)
      ? SUBSCRIPTION_ALLOWED_METHODS.filter((method) => availableMethodTypes.includes(method))
      : [...SUBSCRIPTION_ALLOWED_METHODS];
    const defaultPaymentMethod = subscriptionMethods.includes('credit_card')
      ? 'credit_card'
      : subscriptionMethods[0] || '';

    return {
      savedUniquePaymentState: savedUniqueState,
      paymentMethods: subscriptionMethods,
      defaultPaymentMethod,
    };
  }

  if (
    nextPaymentType === 'unique' &&
    previousPaymentType === 'subscription' &&
    savedUniquePaymentState?.methods?.length
  ) {
    const restoredMethods = savedUniquePaymentState.methods;
    const defaultPaymentMethod = restoredMethods.includes(savedUniquePaymentState.defaultMethod)
      ? savedUniquePaymentState.defaultMethod
      : restoredMethods[0] || '';

    return {
      savedUniquePaymentState,
      paymentMethods: restoredMethods,
      defaultPaymentMethod,
    };
  }

  return {
    savedUniquePaymentState,
    paymentMethods: methods,
    defaultPaymentMethod: currentDefaultMethod,
  };
}
